import BaseModule from '../_base_module';

/**
 * The three booking-confirmation stages, as an operator edits them (BOOK-07).
 *
 * This is APP CONFIGURATION, not member data: six integers that decide when the
 * ~280 members of this gym are asked to confirm a training and when an
 * unconfirmed place would come free. It is reachable only from
 * `/admin/booking-stages`, which is gated by `requireSuperAdmin` on the client
 * and by the API's `super_admin` middleware on the server. **The middleware is
 * the only real control** -- vuex-persist mirrors `current_user` into
 * localStorage with no reducer, so the client-side condition is UX.
 *
 * ============================================================================
 *  A LARGER OFFSET IS EARLIER IN TIME
 * ============================================================================
 *
 * Every value here is minutes BEFORE the training starts (D-41-04). Stage 1's
 * seeded 4320 is T-72h and stage 3's 30 is T-30min. This module compares none of
 * them -- the ordering rules live in `UpdateBookingStagesRequest` on the server
 * and, as a summary line that stops an operator reaching it, on the page -- but
 * the sentence belongs beside the numbers wherever they appear.
 *
 * THE SHAPE IS COPIED FROM `notification_preferences.js`: the isolated state
 * factory, the `suppress_error_toast` fetch that resolves rather than rejects,
 * the server's echo replacing local state wholesale, and the `clear` override.
 * **What is deliberately NOT copied is its optimistic write.** See `save`.
 *
 * THE PHASE-42 RELEASE SWITCH IS PRESENT SINCE 42-07, AND THAT IS THE INTENDED
 * SEQUENCE RATHER THAN A REGRESSION.
 *
 * Until this plan the paragraph here said the switch was absent from this file,
 * from the request and from the response, and that the absence was a control
 * (SC5): a phase-41 screen that could flip that column would have been a way to
 * skip phase 42's go/no-go review over the dry-run ledger.
 *
 * PHASE 42 IS THAT REVIEW (42-UI-SPEC IC3). 42-05 made both flags readable and
 * writable through GET/POST /booking-stages and put five keys on
 * BookingStageResource; 42-07 renders them as the two checkboxes below each
 * stage's four number inputs. The lock was NOT simply removed -- it was replaced
 * by the two things it stood in for: a Dutch confirmation dialog on the
 * on-direction only (S4), and a PgLog audit row on every change to the release
 * flag, written server-side by 42-05.
 *
 * WHAT THIS MODULE STILL DOES NOT DO. It holds no draft, compares nothing and
 * flips nothing on its own. The page owns the one draft; this module carries the
 * server's rows in and the operator's five-key rows out, in ONE request for all
 * three stages (IC7). There is no per-flag action and none may be added: an
 * offset and a release flag that disagree is a combination no single save would
 * have accepted.
 */

/**
 * State as a factory, not a shared object.
 *
 * The older modules in this store export a plain object, so every Vuex store
 * created in the same process shares one state instance -- fine in the app,
 * which has exactly one store, and a source of cross-test contamination
 * everywhere else. This module opts into the isolated form, like `push` and
 * `notification_preferences`.
 */
const state = () => ({
  ...BaseModule.state,

  // The three rows exactly as the server sent them, in the server's order
  // (ordered by `stage` regardless of the order the client submitted).
  data: [],

  // A fetch has completed -- successfully or not. Distinct from `load_failed` so
  // the screen can tell "still loading" (preloader) from "loaded and
  // empty-because-broken".
  loaded: false,

  // The last fetch failed, or came back malformed. The screen renders its S12
  // failure block; deliberately no toast, since S12 is the only surface for a
  // load failure.
  load_failed: false,

  // A save is in flight. ONE save for all three stages (IC7), so this is a plain
  // boolean rather than notification_preferences' per-row key: there is no
  // per-stage save control and none may be added later by accident.
  saving: false,
});

const getters = {
  ...BaseModule.getters,

  all(state) {
    return state.data;
  },

  saving(state) {
    return state.saving;
  },

  has_loaded(state) {
    return state.loaded;
  },

  load_failed(state) {
    return state.load_failed;
  },
};

/**
 * D-41-01 guarantees three rows and gives no way to create or delete one, so a
 * response carrying any other number is a contract regression rather than an
 * empty state.
 */
const EXPECTED_STAGES = 3;

const actions = {
  ...BaseModule.actions,

  /**
   * Load all three stages (BOOK-07).
   *
   * Resolves rather than rejects on failure. The caller is the page's
   * `mounted()`, the screen renders its own S12 failure block off `load_failed`,
   * and there is nothing further up to rethrow to -- an unhandled rejection here
   * would only be a console error on top of the block the operator is already
   * looking at.
   */
  fetch_all({commit, dispatch}) {
    commit('set_state', {property: 'load_failed', value: false});

    return dispatch('api/getEndpoint', {
      endpoint: '/booking-stages',
      // The screen's own failure block (S12) is the only surface for a load
      // failure, so the global danger toast from api/onError would be a second,
      // redundant message about the same request.
      suppress_error_toast: true,
    }, {root: true})
      .then((response) => {
        const stages = response?.data?.data;

        // FEWER (or more) THAN THREE IS MALFORMED, NOT AN EMPTY STATE. D-41-01
        // guarantees three rows and no row can be created or deleted, so
        // anything else is a contract regression -- and IC8 is explicit that
        // such a response renders the failure block and never a partial form.
        // A form built from two stages would let an operator save a
        // configuration the sweep cannot use.
        if (!Array.isArray(stages) || stages.length !== EXPECTED_STAGES) {
          commit('set_state', {property: 'data', value: []});
          commit('set_state', {property: 'load_failed', value: true});

          return null;
        }

        commit('set_state', {property: 'data', value: stages});
        commit('set_state', {property: 'load_failed', value: false});

        return stages;
      })
      .catch(() => {
        commit('set_state', {property: 'data', value: []});
        commit('set_state', {property: 'load_failed', value: true});

        return null;
      })
      .finally(() => {
        commit('set_state', {property: 'loaded', value: true});
      });
  },

  /**
   * Write all three stages, in ONE request (IC7).
   *
   * ---------------------------------------------------------------------------
   * DELIBERATELY NOT OPTIMISTIC, and that is a divergence from the module this
   * one is otherwise copied from.
   * ---------------------------------------------------------------------------
   * `notification_preferences.js:set_category` flips local state first, holds a
   * `previous` snapshot and rolls back on failure. That is right for a toggle:
   * there is no save button, the feedback has to be instant, and a wrong flip is
   * reversible in one tap. It is wrong here. This screen has a save button, so
   * the operator is already waiting; and what is at stake is a stored
   * configuration they will READ BACK to decide whether it is right. A local
   * value that looks saved and is not is exactly the failure IC7 exists to
   * prevent -- so nothing moves until the server has answered, and what lands is
   * the server's echo rather than what was typed.
   *
   * NO `suppress_error_toast`. The global handler's message is the one message a
   * save failure gets; this module adds none of its own and the page adds none
   * either. Re-thrown so the page can keep the operator's draft on screen.
   *
   * FIVE KEYS PER STAGE SINCE 42-07, AND ALL FIVE ARE REQUIRED. 42-05's
   * `UpdateBookingStagesRequest` marks both flags `required` rather than
   * `sometimes`, precisely because IC7's one save covers all three stages -- so a
   * three-key body is a 422, not a partial update. Anything sending less than
   * the five keys below has been broken since 42-05 shipped.
   *
   * @param {{stages: Array<{stage: number, fire_offset_minutes: number, release_offset_minutes: number, releases_for_real: boolean, refund_credits: boolean}>}} payload
   * @returns {Promise<Array>}
   */
  save({commit, dispatch}, {stages}) {
    commit('set_state', {property: 'saving', value: true});

    return dispatch('api/postEndpoint', {
      endpoint: '/booking-stages',
      // ALL THREE, ALWAYS. The ordering rules BETWEEN stages are only checkable
      // as a set, and the server enforces `size:3` for the same reason.
      data: {stages},
    }, {root: true})
      .then((response) => {
        const stored = response?.data?.data;

        // THE SERVER IS AUTHORITATIVE. Its echo replaces `data` wholesale rather
        // than being merged, so what the operator reads back after a save is
        // what the next sweep will actually use -- which is what makes SC3
        // visible instead of merely asserted.
        //
        // Guarded only against a malformed body: replacing a rendered form with
        // `undefined` would blank the screen on what was, by status code, a
        // successful save.
        if (Array.isArray(stored) && stored.length === EXPECTED_STAGES) {
          commit('set_state', {property: 'data', value: stored});
        }

        return stored;
      })
      .finally(() => {
        commit('set_state', {property: 'saving', value: false});
      });
  },
};

const mutations = {
  ...BaseModule.mutations,

  /**
   * Override of BaseModule.mutations.clear. NOT redundant -- do not delete it.
   *
   * The base version resets `state.data` alone. This module has four keys and
   * the other three are exactly the ones that leak. vuex-persist runs with no
   * reducer (src/store/index.js), so the whole store is mirrored into
   * localStorage: a logout leaving `loaded: true` behind makes the next visit
   * skip its preloader and paint a stale form, and a stale `saving: true` leaves
   * the save button disabled for somebody who never pressed it.
   *
   * Stage configuration is not member data, unlike `notification_preferences` --
   * so the reason here is staleness rather than privacy. The defect is the same
   * one, and so is the fix.
   */
  clear(state) {
    state.data = [];
    state.loaded = false;
    state.load_failed = false;
    state.saving = false;
  },
};

export default {

  namespaced: true,

  state,
  getters,
  actions,
  mutations,
};
